'use client'

import { Checkbox } from '@/components/ui/checkbox'
import { Select } from '@/components/ui/select'
import { useProductDetail } from '@/hooks/zustands/use-product-detail'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice, stringToSlug } from '@/lib/utils'
import { ProductPromotionGift } from '@/types/product-detail'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useProductDetail as useProductDetailContext } from '../../context/product-detail-context'

interface ProductPromotionGiftItemProps {
  gift: ProductPromotionGift
  isSelected: boolean
}

const selectClassName = {
  trigger: 'h-[30px] w-fit gap-4 bg-light px-4 py-2 text-xs leading-4',
}

export function ProductPromotionGiftItem({
  gift,
  isSelected,
}: ProductPromotionGiftItemProps) {
  const { setGiftData } = useProductDetail()
  const { product, colorSelected, sizeSelected } = useProductDetailContext()
  const [color, setColor] = useState(gift.colorOptions?.[0]?.value || '')
  const [size, setSize] = useState(gift.sizeOptions?.[0]?.value || '')

  // Chọn màu sản phẩm chính thì chọn luôn màu đó cho quà tặng (nếu quà có màu đó)
  // colorSelected là slug nên phải so theo slug của màu quà tặng
  const [prevColorSelected, setPrevColorSelected] = useState<string | null>(
    null
  )
  if (colorSelected !== prevColorSelected) {
    setPrevColorSelected(colorSelected)
    const matchedColor = colorSelected
      ? gift.colorOptions?.find(
          (option) => stringToSlug(option.value) === colorSelected
        )
      : undefined
    if (matchedColor) {
      setColor(matchedColor.value)
    }
  }

  // Chọn size sản phẩm chính thì chọn luôn size đó cho quà tặng (nếu quà có size đó)
  // Set đồ chọn size theo từng món (comboSelectedInfo) nên chỉ đồng bộ màu, không đồng bộ size
  const [prevSizeSelected, setPrevSizeSelected] = useState<string | null>(null)
  if (sizeSelected !== prevSizeSelected) {
    setPrevSizeSelected(sizeSelected)
    if (
      !product?.is_setdo &&
      sizeSelected &&
      gift.sizeOptions?.some((option) => option.value === sizeSelected)
    ) {
      setSize(sizeSelected)
    }
  }

  const thumbnail =
    gift.colorOptions?.find((option) => option.value === color)?.image ||
    gift.thumbnail

  useEffect(() => {
    const updateGiftOptionId = (color: string, size: string) => {
      const variant = gift.variants.find(
        (v) => v.option1 === color && v.option2 === size
      )

      if (variant) {
        setGiftData({
          id: gift.id,
          optionId: variant.id,
          price: gift.compare_price,
        })
      }
    }

    if (isSelected) {
      updateGiftOptionId(color, size)
    }
  }, [color, size, isSelected, gift, setGiftData])

  return (
    <div className='flex items-center gap-2'>
      {/* Quà tặng luôn được chọn, không cho bỏ tick */}
      <Checkbox
        checked
        tabIndex={-1}
        aria-readonly
        aria-label={gift.display_name}
        className='pointer-events-none'
      />

      <div className='flex min-w-0 flex-1 items-center gap-4'>
        <div className='relative size-16 shrink-0'>
          <Image
            src={ImageHelper.getImageUrl(thumbnail, '', 160)}
            alt={gift.display_name}
            className='size-16 rounded-lg bg-neutral-300 object-cover'
            width={64}
            height={64}
          />
          <Image
            src='/icons/icon-gift-secondary.svg'
            alt=''
            width={16}
            height={16}
            className='absolute left-1 top-1 size-4'
          />
        </div>

        <div className='flex min-w-0 flex-1 flex-col'>
          <p className='line-clamp-1 text-sm leading-5 text-neutral-500'>
            {gift.display_name}
          </p>

          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              {gift.colorOptions && gift.colorOptions.length > 0 && (
                <Select
                  options={gift.colorOptions}
                  value={color}
                  onValueChange={setColor}
                  className={selectClassName}
                />
              )}
              {gift.sizeOptions && gift.sizeOptions.length > 0 && (
                <Select
                  options={gift.sizeOptions}
                  value={size}
                  onValueChange={setSize}
                  className={selectClassName}
                />
              )}
            </div>

            <div className='flex shrink-0 flex-col items-end'>
              <span className='text-base font-bold leading-6 text-neutral-500'>
                {formatPrice(gift.regular_price)}
              </span>
              <span className='text-sm leading-5 text-[#9ca3af] line-through'>
                {formatPrice(gift.compare_price)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
